import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import { useHistory, withRouter } from 'react-router-dom';

import { mapProps } from '../../../../../../../util/hocs';
import { getCheckoutPreviousCart } from '../../../../../../../services/checkout/redux/checkoutSelectors';
import { getAsset } from '../../../../../../../util/assets';
import CART_ITEM from '../../../../../../../services/checkout/constants/cartItems';
import { getAlbumCustomPrintName } from '../../../../../../../util/custom-prints';
import Button from '../../../../../../../components/global/Button';
import CustomPrintBadge from '../../../../../components/CustomPrintBadge';
import { clearCart as clearCartAction } from '../../../../../../../services/checkout/redux/checkoutActions';
import useScrollToTop from '../../../../../../../hooks/useScrollToTop';
import DownloadPhotosHelp, {
  DOWNLOAD_PHOTOS_HELP_OPTIONS,
} from '../DownloadPhotosHelp';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../../../../constants/print-packages';

const propTypes = {
  previousCart: PropTypes.array.isRequired,
  clearCart: PropTypes.func.isRequired,
  albumMembershipGroupId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
};

const CheckoutSuccessMain = ({
  previousCart,
  clearCart,
  albumMembershipGroupId,
  onClose,
}) => {
  useScrollToTop([]);
  const history = useHistory();

  // Clear the cart when this component unmounts, i.e. when the uses has
  // navigated away from this page.
  useEffect(() => {
    return () => clearCart();
  }, []);

  const albumFolderSubscriptionItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION,
  );
  const albumSubscriptionItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_SUBSCRIPTION,
  );
  const albumCustomPrintItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_CUSTOM_PRINT,
  );
  const purchasedCustomPrintItem = previousCart.find(
    item => item.type === CART_ITEM.PURCHASED_CUSTOM_PRINT,
  );
  const albumPrintPackageItem = previousCart.find(
    item => item.type === CART_ITEM.ALBUM_PRINT_PACKAGE,
  );
  const purchasedPrintPackageItem = previousCart.find(
    item => item.type === CART_ITEM.PURCHASED_PRINT_PACKAGE,
  );
  const individualPrintItem = previousCart.find(
    item => item.type === CART_ITEM.INDIVIDUAL_PRINT,
  );
  const legacyPrintItem = previousCart.find(
    item => item.type === CART_ITEM.LEGACY_PRINT,
  );
  const digitalDownloadItems = previousCart.find(
    item => item.type === CART_ITEM.DIGITAL_DOWNLOAD,
  );

  const printPackagesWithDownload = previousCart.find(
    item =>
      [
        CART_ITEM.ALBUM_PRINT_PACKAGE,
        CART_ITEM.PURCHASED_PRINT_PACKAGE,
      ].includes(item.type) &&
      (item.albumPrintPackage.print_package.digital_downloads_mode ===
        DIGITAL_DOWNLOADS_MODE.INCLUDED ||
        item.includeDigitalDownload),
  );

  const purchasedAlbumMembershipGroupId =
    albumFolderSubscriptionItem?.albumFolder.id ||
    albumSubscriptionItem?.album.id;

  const hasPrints =
    !!albumPrintPackageItem ||
    !!purchasedPrintPackageItem ||
    !!purchasedCustomPrintItem ||
    !!individualPrintItem ||
    !!legacyPrintItem;

  const hasPurchasedDownloads =
    !!digitalDownloadItems ||
    !!purchasedAlbumMembershipGroupId ||
    !!printPackagesWithDownload;

  return (
    <div className="memberships-checkout-success-wrap">
      <div className="memberships-checkout-success-header">
        <div className="mb-3">
          <img
            src={getAsset('/waldo-excited.png')}
            width="60px"
            className=""
            alt="Waldo Icon"
          />
        </div>

        <h2 className="text-extra-bold">Thanks for your purchase!</h2>

        {hasPrints && (
          <span className="text-md">
            Expect your prints to be delivered to you pronto!
          </span>
        )}

        {albumCustomPrintItem?.configuration && (
          <span className="text-md">
            Expect your{' '}
            {getAlbumCustomPrintName(albumCustomPrintItem?.albumCustomPrint)} to
            be delivered to you pronto!
          </span>
        )}

        {!hasPurchasedDownloads && (
          <div className="memberships-checkout-success-copy">
            <Button
              to={`/consumer/matches/${albumMembershipGroupId}`}
              className="mt-2 btn-memberships-checkout-success-custom-print"
              replace
              btnBlock
              btnStyle="info"
              btnSize="lg"
            >
              Get back to your photos
            </Button>
          </div>
        )}

        <div className="memberships-checkout-success-extra">
          {albumCustomPrintItem && !albumCustomPrintItem?.configuration && (
            <div className="memberships-checkout-success-custom-print">
              <h3 className="text-extra-bold">
                <CustomPrintBadge className="mr-1" />

                <span>
                  Configure your{' '}
                  {getAlbumCustomPrintName(
                    albumCustomPrintItem?.albumCustomPrint,
                  )}
                  !
                </span>
              </h3>

              <Button
                btnBlock
                btnStyle="info"
                btnSize="lg"
                className="btn-memberships-checkout-success-custom-print"
                to="/consumer/custom-prints/redirect"
              >
                Start here
              </Button>
            </div>
          )}

          {hasPurchasedDownloads && (
            <DownloadPhotosHelp
              onViewPhotos={() => {
                onClose?.();

                history.replace(
                  purchasedAlbumMembershipGroupId
                    ? `/consumer/matches/${purchasedAlbumMembershipGroupId}`
                    : `/consumer/matches/${albumMembershipGroupId}`,
                );
              }}
              options={[
                DOWNLOAD_PHOTOS_HELP_OPTIONS.APP_DOWNLOAD,
                DOWNLOAD_PHOTOS_HELP_OPTIONS.BROWSER,
                digitalDownloadItems || printPackagesWithDownload
                  ? DOWNLOAD_PHOTOS_HELP_OPTIONS.EMAIL
                  : undefined,
              ].filter(Boolean)}
            />
          )}

          <img
            src={getAsset('/groove-blue.svg')}
            alt=""
            className="memberships-checkout-success-groove memberships-checkout-success-groove-blue"
          />
        </div>
      </div>
    </div>
  );
};

CheckoutSuccessMain.propTypes = propTypes;

const mapStateToProps = state => ({
  previousCart: getCheckoutPreviousCart(state),
});

const mapDispatchToProps = {
  clearCart: clearCartAction,
};

export default compose(
  withRouter,
  mapProps(({ match, ...props }) => ({
    albumMembershipGroupId: match.params.albumMembershipGroupId,
    ...props,
  })),
  connect(mapStateToProps, mapDispatchToProps),
)(CheckoutSuccessMain);
